var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import LineItemWrapper from "./LineItemWrapper";
import LineControlConfig from "./LineControlConfig";
/**
 * LineControlWrapper
 *  Encapsulates a LineControl data object with a LineControlConfig configuration object. Provides functionalities such as line item wrapper generation, total computations, line item addition and deletion.
 **/
var LineControlWrapper = /** @class */ (function () {
    /**
     * Create a new LineControl wrapper.
     * @param control Object to wrap.
     * @param config Configuration settings.
     */
    function LineControlWrapper(control, config) {
        if (config === void 0) { config = null; }
        if (!control) {
            throw new Error("Failed to initialize LineControlWrapper: control must be provided.");
        }
        this._control = control;
        this._config = config ? config : new LineControlConfig();
    }
    Object.defineProperty(LineControlWrapper.prototype, "columns", {
        /**
         * Gets the available columns
         */
        get: function () {
            return this._control.columnHeaders;
        },
        enumerable: false,
        configurable: true
    });
    /**
    * Gets the total for the given column. Throws an error if column not found.
    */
    LineControlWrapper.prototype.columnTotal = function (column) {
        var columnIndex = this._control.columnHeaders.indexOf(column);
        var columnTotal = this.columnTotals[columnIndex];
        if (columnTotal) {
            return columnTotal.total;
        }
        else {
            throw new Error("No total found for " + column.value + ".");
        }
    };
    Object.defineProperty(LineControlWrapper.prototype, "columnTotals", {
        /**
         * Gets the column totals
         */
        get: function () {
            var e_1, _a;
            var totals = [];
            var computation = this._config.columnTotalFn;
            try {
                for (var _b = __values(this._control.columnHeaders), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var column = _c.value;
                    var columnIndex = this._control.columnHeaders.indexOf(column);
                    if (columnIndex == -1) {
                        throw new Error("Column " + column.value + " not found");
                    }
                    var columnItems = this.lineItems
                        .map(function (lineItem) { return lineItem.amountItems[columnIndex]; });
                    totals.push({
                        column: column,
                        total: computation(columnItems)
                    });
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_1) throw e_1.error; }
            }
            return totals;
        },
        enumerable: false,
        configurable: true
    });
    LineControlWrapper.prototype.columnTotalFor = function (amountType) {
        var foundTotal = this.columnTotals.find(function (total) { return total.column.type == amountType; });
        if (foundTotal) {
            return foundTotal.total;
        }
        else {
            throw new Error("Column total not found for amount item type: " + amountType);
        }
    };
    Object.defineProperty(LineControlWrapper.prototype, "lineItems", {
        get: function () {
            var e_2, _a;
            var items = [];
            try {
                for (var _b = __values(this._control.lineItems), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var item = _c.value;
                    var config = this._config.getLineItemConfigOrDefault(item.key);
                    items.push(new LineItemWrapper(item, config));
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_2) throw e_2.error; }
            }
            return items;
        },
        enumerable: false,
        configurable: true
    });
    LineControlWrapper.prototype.line = function (lineType) {
        var selector = function (lineItem) { return lineItem.type == lineType; };
        var items = this._control.lineItems.filter(selector);
        if (items.length != 1) {
            throw new Error("Line selector matches multiple lines!");
        }
        else {
            var item = items[0];
            var config = this._config.getOrCreateLineItemConfig(item.key);
            return new LineItemWrapper(item, config);
        }
    };
    /**
    * Gets the total for the line item. Throws an error if column not found.
    */
    LineControlWrapper.prototype.lineTotal = function (key) {
        var index = this.findLineItemIndex(key);
        var lineTotal = this.lineTotals[index];
        if (lineTotal) {
            return lineTotal.total;
        }
        else {
            throw new Error("No total found for " + key + ".");
        }
    };
    Object.defineProperty(LineControlWrapper.prototype, "lineTotals", {
        /**
         * Gets the line item totals
         */
        get: function () {
            var e_3, _a;
            var totals = [];
            try {
                for (var _b = __values(this._control.lineItems), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var lineItem = _c.value;
                    var key = lineItem.key;
                    totals.push({
                        key: key,
                        total: this.computeLineTotal(key)
                    });
                }
            }
            catch (e_3_1) { e_3 = { error: e_3_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_3) throw e_3.error; }
            }
            return totals;
        },
        enumerable: false,
        configurable: true
    });
    LineControlWrapper.prototype.computeColumnTotalWith = function (computation) {
        this._config.columnTotalFn = computation;
    };
    /**
     * Computes the column total
     * TODO: Refactor to use Vue props in a smart way
     * Using functions causes totals to be counted #cols times
     * when we only need to do 1 computation
     */
    LineControlWrapper.prototype.computeLineTotal = function (key) {
        var index = this.findLineItemIndex(key);
        if (index == -1) {
            throw new Error("Line item " + key + " not found");
        }
        return this._control.lineItems[index].amountItems
            .map(function (amountItem) {
            var value = amountItem.amount;
            return Number.isInteger(value) ? value : 0;
        })
            .reduce(function (prev, next) { return prev + next; });
    };
    Object.defineProperty(LineControlWrapper.prototype, "grandTotal", {
        get: function () {
            return this.lineTotals
                .map(function (lineTotal) { return lineTotal.total; })
                .reduce(function (prevTotal, nextTotal) { return prevTotal + nextTotal; });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LineControlWrapper.prototype, "key", {
        get: function () {
            return this._control.key;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LineControlWrapper.prototype, "itemHeaderKey", {
        get: function () {
            return this._control.itemHeaderKey;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LineControlWrapper.prototype, "isReadOnly", {
        get: function () {
            return this._control.isReadOnly;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LineControlWrapper.prototype, "showRowTotals", {
        get: function () {
            return this._config.showRowTotals;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LineControlWrapper.prototype, "showColumnTotals", {
        get: function () {
            return this._config.showColumnTotals;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LineControlWrapper.prototype, "columnTotalKey", {
        get: function () {
            return this._control.columnTotalKey;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LineControlWrapper.prototype, "showActions", {
        get: function () {
            return this._config.showActions;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LineControlWrapper.prototype, "maxRows", {
        get: function () {
            return this._config.maxRows;
        },
        enumerable: false,
        configurable: true
    });
    LineControlWrapper.prototype.addLineItem = function () {
        var factory = this._config.lineItemFactory;
        if (!factory) {
            throw new Error("Line item factory is not defined.");
        }
        var lineItem = factory(this.columns);
        this._control.lineItems.push(lineItem);
    };
    LineControlWrapper.prototype.removeLineItem = function (key) {
        var index = this.findLineItemIndex(key);
        if (index == -1) {
            throw new Error("Line item " + key + " not found");
        }
        else {
            this._control.lineItems.splice(index, 1);
        }
    };
    LineControlWrapper.prototype.findLineItemIndex = function (key) {
        return this._control.lineItems
            .findIndex(function (item) { return item.key == key; });
    };
    return LineControlWrapper;
}());
export default LineControlWrapper;
